import { useMemo } from "react";
import type { Editor } from "@tiptap/react";
import { t } from "@/i18n-js/instance";
import { Icon } from "@circle-react/components/shared/Icon";
import {
  Spinner,
  Timer,
} from "@circle-react/components/shared/uikit/TipTap/VoiceMessage/components";
import { generateRandomString } from "@circle-react/components/shared/uikit/TipTap/VoiceMessage/helpers";
import { useRecorder } from "@circle-react/components/shared/uikit/TipTap/VoiceMessage/hooks";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useBlockEditorWebsocket } from "./useBlockEditorWebsocket";

interface VoiceMessageRecorderProps {
  addToLocalInlineAttachments: (attachment: any) => void;
  editor: Editor;
  onClose: () => void;
  range: { from: number; to: number };
}

export const VoiceMessageRecorder = ({
  onClose,
  addToLocalInlineAttachments,
  editor,
  range,
}: VoiceMessageRecorderProps) => {
  const recordingId = useMemo(() => generateRandomString(), []);

  const { isRecording, status, isBusy, isError, onSubmit, setServerError } =
    useRecorder({ recordingId });
  useBlockEditorWebsocket({
    onClose,
    recordingId,
    setServerError,
    addToLocalInlineAttachments,
    editor,
    range,
  });

  const statusLabel = t(`tiptap.voice_message.${status}`);

  return (
    <div className="bg-primary shadow-voice-message absolute left-0 top-0 z-10 flex size-full items-center justify-center rounded-lg">
      <div
        className={classNames(
          "flex items-center rounded-full bg-gray-100 p-1 text-gray-700 dark:bg-gray-900 dark:text-gray-300",
          {
            "text-red-600 dark:text-red-600": isRecording,
          },
        )}
      >
        <button
          type="button"
          onClick={onClose}
          className="bg-primary flex items-center justify-center rounded-full p-2"
        >
          <Icon
            type="20-close"
            viewBox="0 0 20 20"
            className="text-icon dark:text-white"
          />
        </button>
        <div className="ml-4 mr-2 flex">
          <div className="min-w-[160px]">{statusLabel}</div>
          <div className="flex">
            <div className="mr-2 min-w-[45px]">
              <Timer isRecording={isRecording} />
            </div>
            <Icon
              type="20-microphone"
              className={classNames(
                "mt-[2px] text-gray-700 dark:text-gray-300",
                {
                  "text-red-600 dark:text-red-600": isRecording,
                },
              )}
            />
          </div>
        </div>
        <button
          type="submit"
          className="bg-brand flex items-center justify-center rounded-full p-2"
          onClick={onSubmit}
          disabled={isBusy}
        >
          {isBusy ? (
            <Spinner />
          ) : (
            <Icon
              type={isError ? "20-retry" : "20-checkmark"}
              viewBox="0 0 20 20"
              className="text-white"
            />
          )}
        </button>
      </div>
    </div>
  );
};
